import { customElement } from '@adobe/edex/ui/edex-lit';
import { html } from '@adobe/edex/ui/edex-lit';
import { ifDefined } from '@adobe/edex/ui/edex-lit';
import { EdexTagName } from '@adobe/edex/ui/shared/constants';

import { $t, getTranslation } from '@adobe/edex/ui/shared/config/i18n';
import '@spectrum-web-components/checkbox/sp-checkbox.js';
import '@spectrum-web-components/overlay/active-overlay.js';
import '@spectrum-web-components/overlay/overlay-trigger.js';
import '@adobe/edex/ui/components/resource/resource-builder/utils/form-error/form-error';
import { indexMetadataByID } from '@adobe/edex/ui/utils/marshal';
import { getAssetURL } from '@adobe/edex/ui/utils';
import { BaseElement } from '@adobe/edex/ui/components/base.element';
import { TreeSelectorChangePayload } from '@adobe/edex/ui/types/payload';
import { TreeItem, TreeSelectorParams } from '@adobe/edex/ui/components/shared/tree-selector/tree-selector.types';
import { classMap } from '@adobe/edex/ui/edex-lit';
export * from './tree-selector.types';

import(
  /* webpackChunkName: "component/tree-selector-element-styles" */
  /* webpackMode: "lazy" */
  './tree-selector.scss'
);

type TreeParams = TreeSelectorParams;

interface TreeItemParams extends TreeParams {
  item: TreeItem;
  index: number;
}

@customElement(EdexTagName.treeSelector)
export class TreeSelector extends BaseElement {
  get useLightDom(): boolean {
    return true;
  }

  static get properties() {
    return {
      params: { type: Object },
      itemsById: { attribute: false },
    };
  }

  params: TreeSelectorParams;
  expandedIds = new Set<string>();

  get itemsById(): Map<string, TreeItem> {
    return indexMetadataByID(this.params.items);
  }

  getLabelById = (id: string): string => {
    const label = id && this.itemsById?.get(id)?.i18nLabel;
    return label ? getTranslation(label) : '';
  };

  handleClickLabel(e) {
    const { currentTarget } = e;
    const id = currentTarget.getAttribute('for');
    const labelFor: HTMLElement = this.renderRoot.querySelector(`[id="${id}"]`);
    if (!currentTarget.contains(labelFor)) {
      labelFor?.click();
    }
  }

  handleChange = (event: Event) => {
    const {
      checked,
      dataset: { id },
    } = event.target as HTMLInputElement;
    event.stopPropagation();
    const item = this.itemsById.get(id);
    const parent = this.itemsById.get(item.parentID);
    this.emitEvent<TreeSelectorChangePayload>('change', {
      checked,
      id,
      item,
      parent,
    });
  };

  handleTreeToggle = (event: Event) => {
    const li = (event.target as Element).closest('li');
    const { id } = li.dataset;

    if (this.isTreeItemExpanded(id)) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }
    this.requestUpdate();
  };

  isTreeItemExpanded = (id: string) => this.expandedIds.has(id);

  renderTree() {
    const { params } = this;
    return html`
      <ul role="tree" class="filter-tree ${classMap({ inline: params.inline })}" aria-labelledby=${params.label}>
        ${params.items.map((item, index) => this.renderTreeItem({ ...params, item, index }))}
      </ul>
    `;
  }

  renderTreeItem = (params: TreeItemParams) => {
    const { item, items, index, isChecked, isDisabled, isIndeterminate, isUnselectable, isReadonly } = params;
    const { itemsById } = this;
    const { id, i18nLabel, parentID, children } = item;
    const parent = itemsById.get(parentID);
    const level = parentID ? 2 : 1;
    const caret = !isDisabled?.(item, parent) ? getAssetURL('caret.svg') : getAssetURL('caret-disabled.svg');
    const checked = isChecked?.(item, parent);
    const isExpanded = this.isTreeItemExpanded(id);
    const hasChildren = children?.length > 0;
    return html`
      <li
        role="treeitem"
        aria-expanded=${ifDefined(hasChildren ? String(isExpanded) : undefined)}
        aria-level=${level}
        aria-setsize=${items.length}
        aria-posinset=${index + 1}
        tabindex=${level === 1 && index === 0 ? 0 : -1}
        data-id=${id}
      >
        <span>
          ${hasChildren
            ? html`
                <sp-action-button
                  quiet
                  size="s"
                  ?disabled=${isDisabled?.(item, parent)}
                  @click=${this.handleTreeToggle}
                >
                  <img src=${caret} width="32" height="32" alt="" />
                </sp-action-button>
              `
            : null}
          <sp-checkbox
            data-id=${id}
            class=${classMap({
              'unselectable-checkbox': isUnselectable?.(item, parent),
            })}
            ?checked=${checked}
            ?disabled=${isDisabled?.(item, parent)}
            ?readonly=${isReadonly?.(item, parent)}
            ?indeterminate=${!checked && isIndeterminate?.(item, parent)}
            @change=${this.handleChange}
          >
            ${$t(i18nLabel)}
          </sp-checkbox>
        </span>
        ${hasChildren && isExpanded
          ? html`
              <ul role="group">
                ${children.map((child, index) =>
                  this.renderTreeItem({
                    ...params,
                    item: child,
                    items: children,
                    index,
                  })
                )}
              </ul>
            `
          : null}
      </li>
    `;
  };

  render() {
    const {
      params: { label, selected, placeholder, error, inline },
    } = this;
    if (inline) {
      return this.renderTree();
    }
    const value = selected.map(this.getLabelById).filter(Boolean).join(', ');
    return html`
      <div class="tree-selector">
        <sp-field-label for=${label} size="l" @click=${this.handleClickLabel}>${$t(label)}</sp-field-label>
        <overlay-trigger id="trigger" placement="bottom-start">
          <sp-picker slot="trigger" id=${label} label=${value || $t(placeholder)} ?invalid=${!!error}></sp-picker>
          <sp-popover placement="bottom-start" slot="click-content" open>${this.renderTree()}</sp-popover>
        </overlay-trigger>
        <edex-form-error id="${label}Error" .error=${error}></edex-form-error>
      </div>
    `;
  }
}
